/**
 * ZoneMapTab Component
 *
 * Tab component for map-based zone creation
 * Allows users to draw shapes on a recall map and create zones
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useToast } from '@/hooks/use-toast';
import {
  Loader2,
  Map,
  Info,
  ChevronDown,
  Filter,
  Settings,
  Users,
  X,
  Calendar,
  Maximize2,
  Minimize2,
  RefreshCw,
  CircleDot,
  Paintbrush
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { ZoneDrawingMapOptimized } from './ZoneDrawingMapOptimized';
import { useStableDealerClusteringCached } from '../hooks/useCachedRecallData';
import { useZones, useZoneMutations } from '../hooks/useZones';
import { Zone, tenantAPI } from '@/services/api/tenants';
import { normalizeGeometry, isGeometryTooLarge, type GeoJSONGeometry } from '../utils/zoneGeometry';
import { lookupZipCodes } from '../services/zipCodeLookup';
import { calculateZoneStatistics, getZoneSizeLabel } from '../utils/zoneStatistics';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useCampaignPriorities } from '@/hooks/useCampaignPriorities';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';
import { getExcludedDealers } from '@/features/recalls/services/queryBuilder';
import { supabase, getProdSupabaseClient } from '@/lib/supabase/client';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { KNOWN_OEMS, DEFAULT_OEM, getBrandsForOEM } from '@/constants/oems';

// Add CSS for bubbling animation
const styles = `
  @keyframes zipBubble {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
    }
    50% {
      transform: scale(1.05);
      box-shadow: 0 0 0 10px rgba(239, 68, 68, 0);
    }
    100% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
    }
  }
  
  .zip-bubble-animation {
    animation: zipBubble 1.5s infinite;
  }
`;

export const ZoneMapTab: React.FC = () => {
  const { session } = useAuth();
  const { toast } = useToast();
  const { priorities: campaignPreferences } = useCampaignPriorities(session?.user?.id);

  // Use cached zone data
  const { data: zones = [], isLoading: loadingZones, refetch: refetchZones } = useZones();
  const { createZone, updateZone, deleteZone } = useZoneMutations();

  // Get count of excluded dealers and active campaign filters
  const [excludedDealerCount, setExcludedDealerCount] = useState<number>(0);
  const activeCampaignFilters = useMemo(() => {
    if (!campaignPreferences) return 0;
    return Object.keys(campaignPreferences).length;
  }, [campaignPreferences]);

  const [drawnGeometry, setDrawnGeometry] = useState<GeoJSONGeometry | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [zoneName, setZoneName] = useState('');
  const [zoneDescription, setZoneDescription] = useState('');
  const [zoneRegion, setZoneRegion] = useState('');
  const [zoneStats, setZoneStats] = useState<{
    zipCodes: string[];
    totalRecalls: number;
    uniqueVins: number;
    overlappingZones?: { zoneName: string; overlappingZips: string[] }[];
    isLoadingStats?: boolean;
    statsError?: boolean;
  } | null>(null);

  const [selectedZoneId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [zoneToDelete, setZoneToDelete] = useState<Zone | null>(null);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [updatingZoneBoundaries, setUpdatingZoneBoundaries] = useState(false);
  const [shouldClearDrawnShapes, setShouldClearDrawnShapes] = useState(false);
  const [selectedSnapshotDate, setSelectedSnapshotDate] = useState<string | null>(null);
  const [availableSnapshots, setAvailableSnapshots] = useState<
    Array<{ snapshot_date: string; record_count: number }>
  >([]);
  const [loadingSnapshots, setLoadingSnapshots] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showBubbles, setShowBubbles] = useState(true);
  const [uniformColor, setUniformColor] = useState(false);

  // Zone and Region filters
  const [zoneFilter, setZoneFilter] = useState<string[]>([]);
  const [regionFilter, setRegionFilter] = useState<string>('all');
  const [availableRegions, setAvailableRegions] = useState<string[]>([]);

  // OEM filter for zones - determines which OEM's zones to show on map and create zones for
  const [selectedOem, setSelectedOem] = useState<string>(DEFAULT_OEM);

  // Reset zone multi-select when OEM or region changes (selected IDs may no longer be valid)
  useEffect(() => {
    setZoneFilter([]);
  }, [selectedOem, regionFilter]);

  // Hook must be called after state declarations
  const {
    dealerLocations,
    loading: recallsLoading,
    error: recallsError,
    loadViewportData,
    refreshData: refreshRecallData
  } = useStableDealerClusteringCached(session?.user?.id, selectedSnapshotDate, selectedOem);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentViewportBounds, setCurrentViewportBounds] = useState<{
    north: number;
    south: number;
    east: number;
    west: number;
  } | null>(null);
  const previousBoundsRef = useRef<{
    north: number;
    south: number;
    east: number;
    west: number;
  } | null>(null);
  const lastLoadedBoundsRef = useRef<{
    north: number;
    south: number;
    east: number;
    west: number;
  } | null>(null);

  // Helper function to calculate viewport center
  const getViewportCenter = (bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  }) => {
    return {
      lat: (bounds.north + bounds.south) / 2,
      lng: (bounds.east + bounds.west) / 2
    };
  };

  // Helper function to check if viewport has moved significantly
  // Returns true if center moved more than threshold or bounds don't overlap much
  const hasViewportMovedSignificantly = (
    prev: { north: number; south: number; east: number; west: number } | null,
    current: { north: number; south: number; east: number; west: number }
  ): boolean => {
    if (!prev) return true; // First load

    const prevCenter = getViewportCenter(prev);
    const currentCenter = getViewportCenter(current);

    // Check if center moved more than ~2 degrees (significant geographic move)
    const centerDistance = Math.sqrt(
      Math.pow(currentCenter.lat - prevCenter.lat, 2) +
        Math.pow(currentCenter.lng - prevCenter.lng, 2)
    );

    if (centerDistance > 2.0) {
      return true; // Significant move (e.g., Florida to Detroit)
    }

    // Check if bounds overlap significantly
    // Calculate overlap percentage
    const overlapLat = Math.max(
      0,
      Math.min(prev.north, current.north) - Math.max(prev.south, current.south)
    );
    const overlapLng = Math.max(
      0,
      Math.min(prev.east, current.east) - Math.max(prev.west, current.west)
    );

    const prevHeight = prev.north - prev.south;
    const prevWidth = prev.east - prev.west;
    const currentHeight = current.north - current.south;
    const currentWidth = current.east - current.west;

    const overlapArea = overlapLat * overlapLng;
    const prevArea = prevHeight * prevWidth;
    const currentArea = currentHeight * currentWidth;
    const minArea = Math.min(prevArea, currentArea);

    // If overlap is less than 30% of the smaller area, it's a significant move
    const overlapRatio = minArea > 0 ? overlapArea / minArea : 0;

    return overlapRatio < 0.3;
  };

  // Memoize the bounds change handler
  // Load data on significant viewport changes (e.g., moving to different region)
  // But not on every small pan/zoom (which causes cluster breakdown)
  const handleMapBoundsChange = useCallback(
    (bounds: { north: number; south: number; east: number; west: number }) => {
      const prev = previousBoundsRef.current;

      // Always update viewport bounds for zone filtering (client-side)
      previousBoundsRef.current = bounds;
      setCurrentViewportBounds(bounds);

      // Check if bounds changed significantly (prevent infinite loops on tiny movements)
      if (
        prev &&
        Math.abs(prev.north - bounds.north) < 0.0001 &&
        Math.abs(prev.south - bounds.south) < 0.0001 &&
        Math.abs(prev.east - bounds.east) < 0.0001 &&
        Math.abs(prev.west - bounds.west) < 0.0001
      ) {
        return; // Bounds haven't changed - skip
      }

      // Check if viewport has moved significantly (e.g., Florida to Detroit)
      // Only reload data on significant moves, not on every pan/zoom
      const lastLoaded = lastLoadedBoundsRef.current;
      // If no data has been loaded yet, always load (initial load case)
      if (!lastLoaded || hasViewportMovedSignificantly(lastLoaded, bounds)) {
        lastLoadedBoundsRef.current = bounds;
        loadViewportData(bounds);
      }
    },
    [loadViewportData]
  );

  // Filter dealers by OEM - only show dealers with vehicles from selected OEM's brands
  const filteredDealerLocations = useMemo(() => {
    if (!selectedOem) return dealerLocations;

    const oemBrands = getBrandsForOEM(selectedOem);
    if (oemBrands.length === 0) return dealerLocations; // No mapping = show all

    return dealerLocations.filter(dealer => {
      // Check if dealer has any vehicles matching OEM brands
      return dealer.makes.some(make =>
        oemBrands.some(brand => make.toLowerCase() === brand.toLowerCase())
      );
    });
  }, [dealerLocations, selectedOem]);

  // Calculate total dealers and recalls for header (using filtered data)
  const totalDealers = filteredDealerLocations.length;
  const totalRecalls = filteredDealerLocations.reduce(
    (sum, dealer) => sum + (dealer.recallCount || 0),
    0
  );

  // Filter zones by viewport bounds, zone name filter, region filter, and OEM
  const zonesInViewport = useMemo(() => {
    let filtered = zones;

    // Apply OEM filter - only show zones for the selected OEM
    if (selectedOem) {
      filtered = filtered.filter(zone => zone.oem === selectedOem);
    }

    // Apply zone name filter (multi-select: empty array = all zones)
    if (zoneFilter.length > 0) {
      filtered = filtered.filter(zone => zoneFilter.includes(zone.id));
    }

    // Apply region filter
    if (regionFilter && regionFilter !== 'all') {
      filtered = filtered.filter(zone => zone.region === regionFilter);
    }

    // Apply viewport bounds filter
    if (currentViewportBounds) {
      filtered = filtered.filter(zone => {
        const geometry = (zone.metadata as any)?.geometry;
        if (!geometry || !geometry.coordinates) return false;

        // Check if zone geometry intersects with viewport bounds using bounding box
        try {
          // Get bounding box of zone geometry
          const coords = geometry.coordinates;
          let minLat = Infinity;
          let maxLat = -Infinity;
          let minLng = Infinity;
          let maxLng = -Infinity;

          const extractBounds = (coords: any[]): void => {
            if (Array.isArray(coords[0])) {
              coords.forEach(coord => extractBounds(coord));
            } else if (
              coords.length >= 2 &&
              typeof coords[0] === 'number' &&
              typeof coords[1] === 'number'
            ) {
              const [lng, lat] = coords;
              minLat = Math.min(minLat, lat);
              maxLat = Math.max(maxLat, lat);
              minLng = Math.min(minLng, lng);
              maxLng = Math.max(maxLng, lng);
            }
          };

          extractBounds(coords);

          // Check if bounding boxes intersect (if any part of zone is visible in viewport)
          return !(
            maxLat < currentViewportBounds.south ||
            minLat > currentViewportBounds.north ||
            maxLng < currentViewportBounds.west ||
            minLng > currentViewportBounds.east
          );
        } catch (error) {
          // If geometry parsing fails, include the zone to be safe
          return true;
        }
      });
    }

    return filtered;
  }, [zones, currentViewportBounds, zoneFilter, regionFilter, selectedOem]);

  // Invalidate map size when going fullscreen to fix viewport
  useEffect(() => {
    if (isFullscreen) {
      // Small delay to ensure DOM has updated
      const timer = setTimeout(() => {
        // Trigger window resize event to let Leaflet know to recalculate
        window.dispatchEvent(new Event('resize'));
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isFullscreen]);

  // Zones are now loaded via useZones hook with caching

  useEffect(() => {
    // Load initial data for Florida
    const initialBounds = {
      north: 31.0,
      south: 24.5,
      east: -79.5,
      west: -87.6
    };
    setCurrentViewportBounds(initialBounds);
    previousBoundsRef.current = initialBounds;
    lastLoadedBoundsRef.current = initialBounds;
    loadViewportData(initialBounds);
  }, [loadViewportData]);

  // Get excluded dealer count for filter display
  useEffect(() => {
    if (session?.user?.id) {
      getExcludedDealers(supabase, session.user.id)
        .then(dealers => {
          setExcludedDealerCount(dealers.length);
        })
        .catch(() => {
          // Silently handle error
        });
    }
  }, [session?.user?.id]);

  // Load available regions for filter dropdown
  useEffect(() => {
    const loadRegions = async () => {
      try {
        const regions = await tenantAPI.getUniqueRegions();
        setAvailableRegions(regions);
      } catch (error) {
        console.error('Error loading regions:', error);
      }
    };
    loadRegions();
  }, []);

  // Load available snapshot dates for Control Center
  useEffect(() => {
    const loadSnapshots = async () => {
      try {
        setLoadingSnapshots(true);
        const prodClient = getProdSupabaseClient();
        const { data, error } = await prodClient.rpc('get_all_snapshot_dates');

        if (error) {
          console.error('Error loading snapshot dates:', error);
          // Fallback: try using getAvailableSnapshots if RPC doesn't exist
          try {
            const { getAvailableSnapshots } = await import('@/lib/supabase/snapshotConfig');
            const snapshots = await getAvailableSnapshots();
            setAvailableSnapshots(snapshots);
          } catch (fallbackError) {
            console.error('Fallback snapshot loading also failed:', fallbackError);
          }
        } else {
          console.log('[ZoneMapTab] Raw snapshot data from RPC:', data);
          const processedSnapshots = (data || [])
            .map((s: any) => {
              // Handle different date formats (timestamp, date string, Date object, etc.)
              let dateStr: string;

              if (!s.snapshot_date) {
                console.warn('[ZoneMapTab] Missing snapshot_date in record:', s);
                return null;
              }

              if (s.snapshot_date instanceof Date) {
                // If it's a Date object (from PostgreSQL date type), convert to YYYY-MM-DD
                // Use UTC date to avoid timezone issues (PostgreSQL dates are timezone-agnostic)
                const year = s.snapshot_date.getUTCFullYear();
                const month = String(s.snapshot_date.getUTCMonth() + 1).padStart(2, '0');
                const day = String(s.snapshot_date.getUTCDate()).padStart(2, '0');
                dateStr = `${year}-${month}-${day}`;
              } else if (typeof s.snapshot_date === 'string') {
                // If it's a string (ISO timestamp like "2025-12-16T06:00:00.000Z"), extract just the date part
                if (s.snapshot_date.includes('T')) {
                  dateStr = s.snapshot_date.split('T')[0];
                } else if (s.snapshot_date.match(/^\d{4}-\d{2}-\d{2}$/)) {
                  // Already in YYYY-MM-DD format
                  dateStr = s.snapshot_date;
                } else {
                  console.warn('[ZoneMapTab] Unexpected date string format:', s.snapshot_date);
                  return null;
                }
              } else {
                console.warn(
                  '[ZoneMapTab] Unexpected snapshot_date type:',
                  typeof s.snapshot_date,
                  s.snapshot_date
                );
                return null;
              }

              return {
                snapshot_date: dateStr,
                record_count: Number(s.record_count) || 0
              };
            })
            .filter((s): s is { snapshot_date: string; record_count: number } => s !== null);

          console.log('[ZoneMapTab] Processed snapshots:', processedSnapshots);
          console.log(
            '[ZoneMapTab] Available snapshot dates:',
            processedSnapshots.map(s => s.snapshot_date)
          );

          // Sort by date descending (newest first)
          processedSnapshots.sort((a, b) => b.snapshot_date.localeCompare(a.snapshot_date));

          setAvailableSnapshots(processedSnapshots);
        }
      } catch (err) {
        console.error('Failed to load snapshot dates:', err);
      } finally {
        setLoadingSnapshots(false);
      }
    };

    loadSnapshots();
  }, []);

  // Check for ZIP code overlaps with existing zones using server-side validation
  // This is async because it uses an RPC call to the database
  const checkZipCodeOverlapsAsync = useCallback(
    async (
      zipCodesToCheck: string[],
      excludeZoneId?: string
    ): Promise<{ zoneName: string; overlappingZips: string[] }[]> => {
      if (!zipCodesToCheck || zipCodesToCheck.length === 0) {
        console.log('[ZoneMapTab] checkZipCodeOverlapsAsync: No ZIP codes to check');
        return [];
      }

      try {
        console.log('[ZoneMapTab] checkZipCodeOverlapsAsync: Calling server-side validation with', {
          zipCodesToCheck: zipCodesToCheck.length,
          zipCodesSample: zipCodesToCheck.slice(0, 10),
          excludeZoneId,
          oem: selectedOem
        });

        // Pass selectedOem to only check overlaps within the same OEM
        const validation = await tenantAPI.validateZipCodeOverlaps(
          zipCodesToCheck,
          excludeZoneId,
          selectedOem
        );

        console.log('[ZoneMapTab] checkZipCodeOverlapsAsync result:', {
          hasOverlaps: validation.hasOverlaps,
          conflicts: validation.conflicts,
          conflictingZips: validation.conflicts.flatMap(c => c.overlappingZips)
        });

        if (validation.hasOverlaps) {
          return validation.conflicts.map(conflict => ({
            zoneName: conflict.zoneName,
            overlappingZips: conflict.overlappingZips
          }));
        }

        return [];
      } catch (error) {
        console.error('[ZoneMapTab] checkZipCodeOverlapsAsync error:', error);
        return [];
      }
    },
    [selectedOem]
  );

  // Synchronous version for backward compatibility (always returns empty, use async version)
  const checkZipCodeOverlaps = useCallback(
    (
      zipCodesToCheck: string[],
      excludeZoneId?: string
    ): { zoneName: string; overlappingZips: string[] }[] => {
      // This is a placeholder - actual overlap checking is done async via checkZipCodeOverlapsAsync
      // Return empty array to not block, the async version will update state
      console.log(
        '[ZoneMapTab] checkZipCodeOverlaps (sync): Returning empty, use async version for actual check'
      );
      return [];
    },
    []
  );

  // Re-check overlaps when dialog opens or ZIP codes change
  // Uses async server-side validation
  useEffect(() => {
    const checkOverlaps = async () => {
      if (zoneStats && zoneStats.zipCodes && zoneStats.zipCodes.length > 0 && dialogOpen) {
        console.log(
          '[ZoneMapTab] Dialog open, checking overlaps for',
          zoneStats.zipCodes.length,
          'ZIP codes'
        );

        const overlappingZones = await checkZipCodeOverlapsAsync(zoneStats.zipCodes);

        console.log('[ZoneMapTab] Async overlap check result:', {
          zipCodesCount: zoneStats.zipCodes.length,
          overlappingZonesFound: overlappingZones.length,
          overlappingZones: overlappingZones,
          currentOverlaps: zoneStats.overlappingZones?.length || 0
        });

        // Update state if overlaps changed
        if (overlappingZones.length > 0) {
          console.log('[ZoneMapTab] Updating zoneStats with overlaps from server');
          setZoneStats(prev => ({
            ...prev!,
            overlappingZones
          }));
        } else if (zoneStats.overlappingZones && zoneStats.overlappingZones.length > 0) {
          // Clear overlaps if none found
          console.log('[ZoneMapTab] Clearing overlaps (none found on server)');
          setZoneStats(prev => ({
            ...prev!,
            overlappingZones: []
          }));
        }
      }
    };

    checkOverlaps();
  }, [zoneStats?.zipCodes, dialogOpen, checkZipCodeOverlapsAsync]);

  // Handle shape drawn on map
  const handleShapeDrawn = useCallback(
    async (geometry: GeoJSON.Geometry) => {
      try {
        // Normalize geometry (handles LineString → Polygon buffering, cleaning, simplification)
        const normalized = normalizeGeometry(geometry as GeoJSONGeometry);

        // Warn if geometry is too large
        if (isGeometryTooLarge(normalized)) {
          toast({
            title: 'Large Zone Warning',
            description: 'This zone is very large (>1000 sq km). Processing may take longer.',
            variant: 'default'
          });
        }

        setDrawnGeometry(normalized);

        // Lookup ZIP codes
        setCreating(true);
        const zipCodes = await lookupZipCodes(normalized);

        if (zipCodes.length === 0) {
          toast({
            title: 'No ZIP Codes Found',
            description:
              'No ZIP codes were found within the drawn shape. Please try a different area.',
            variant: 'default'
          });
          setCreating(false);
          return;
        }

        // Open dialog immediately with ZIP codes (before checking overlaps)
        // This prevents the dialog from appearing frozen while stats/overlaps are calculated
        const initialZoneStats = {
          zipCodes,
          totalRecalls: 0, // Will be updated when stats are ready
          uniqueVins: 0, // Will be updated when stats are ready
          overlappingZones: [], // Will be updated by useEffect when async check completes
          isLoadingStats: true // Flag to show loading state
        };

        console.log('[ZoneMapTab] Setting initial zoneStats:', {
          zipCodes: initialZoneStats.zipCodes.length
        });

        setZoneStats(initialZoneStats);

        // Always open dialog immediately - overlaps will be checked async by useEffect
        console.log('[ZoneMapTab] Opening zone creation dialog:', {
          zipCodesCount: zipCodes.length,
          zipCodesSample: zipCodes.slice(0, 10)
        });
        setDialogOpen(true);
        setCreating(false); // Allow user to interact with dialog while stats load

        // Calculate zone statistics from database with proper filtering (async, non-blocking)
        // This ensures we get ALL recalls for the zone, not just viewport data
        // Pass selectedOem to filter recalls by OEM brands
        calculateZoneStatistics(
          zipCodes,
          session?.user?.id,
          campaignPreferences,
          selectedSnapshotDate,
          selectedOem
        )
          .then(stats => {
            // Update stats when ready - preserve overlappingZones
            setZoneStats(prev => {
              if (!prev) {
                console.error('[ZoneMapTab] prev is null when updating stats!');
                return prev;
              }

              const updated = {
                ...prev,
                totalRecalls: stats.totalRecalls,
                uniqueVins: stats.uniqueVins,
                isLoadingStats: false,
                // Explicitly preserve overlappingZones
                overlappingZones: prev.overlappingZones
              };

              console.log('[ZoneMapTab] Updated zoneStats with stats:', {
                prevOverlaps: prev.overlappingZones,
                prevOverlapsLength: prev.overlappingZones?.length || 0,
                updatedOverlaps: updated.overlappingZones,
                updatedOverlapsLength: updated.overlappingZones?.length || 0,
                preserved: prev.overlappingZones === updated.overlappingZones,
                overlappingZips: updated.overlappingZones?.flatMap(z => z.overlappingZips) || []
              });

              return updated;
            });

            // Show warning toast if zone is too large, but still allow dialog
            if (stats.totalRecalls >= 700) {
              toast({
                title: 'Zone Size Warning',
                description: `This zone has ${stats.totalRecalls.toLocaleString()} recalls (Extra Large). Consider breaking it into smaller zones for better management. Ideally: Small (< 300), Medium (300-499), or Large (500-699).`,
                variant: 'destructive',
                duration: 8000
              });
            }
          })
          .catch(error => {
            console.error('[ZoneMapTab] Error calculating zone statistics:', error);
            // Update to show error state - don't allow zone creation with 0 recalls
            setZoneStats(prev => ({
              ...prev!,
              isLoadingStats: false,
              statsError: true
            }));
            toast({
              title: 'Stats Calculation Failed',
              description:
                'Could not calculate zone statistics. Please try again or check the browser console for details.',
              variant: 'destructive'
            });
          });
      } catch (error: any) {
        toast({
          title: 'Error Processing Shape',
          description: error.message || 'Failed to process the drawn shape',
          variant: 'destructive'
        });
        setCreating(false);
      }
    },
    [
      session?.user?.id,
      toast,
      campaignPreferences,
      selectedSnapshotDate,
      checkZipCodeOverlaps,
      zones
    ]
  );

  // Handle zone creation
  const handleCreateZone = useCallback(async () => {
    if (!zoneName.trim() || !drawnGeometry || !zoneStats) {
      toast({
        title: 'Validation Error',
        description: 'Zone name is required',
        variant: 'destructive'
      });
      return;
    }

    if (!zoneRegion.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Region is required',
        variant: 'destructive'
      });
      return;
    }

    // Check for extremely large zones that might cause database issues
    if (zoneStats.zipCodes.length > 500) {
      toast({
        title: 'Zone Extremely Large',
        description: `This zone has ${zoneStats.zipCodes.length} ZIP codes. Zones this large may cause technical issues. Please consider breaking it down.`,
        variant: 'destructive'
      });
      return;
    }

    // Check for ZIP code overlaps with existing zones using server-side validation
    // Note: We already check in useEffect when dialog opens, but double-check here before creating
    const overlappingZones = await checkZipCodeOverlapsAsync(zoneStats.zipCodes);
    if (overlappingZones.length > 0) {
      const totalOverlaps = overlappingZones.reduce(
        (sum, zone) => sum + zone.overlappingZips.length,
        0
      );

      // Update zoneStats with overlaps so they show in the dialog (highlighted in red)
      // Keep dialog open so user can see and fix the conflicts
      console.log('[ZoneMapTab] Overlaps found in handleCreateZone, updating zoneStats:', {
        overlappingZones,
        overlappingZonesCount: overlappingZones.length,
        totalOverlaps,
        allConflictingZips: overlappingZones.flatMap(z => z.overlappingZips)
      });

      setZoneStats(prev => ({
        ...prev!,
        overlappingZones
      }));

      // Ensure dialog stays open
      setDialogOpen(true);

      toast({
        title: 'ZIP Code Overlap Detected',
        description: `${totalOverlaps} ZIP code${totalOverlaps === 1 ? '' : 's'} already exist${totalOverlaps === 1 ? 's' : ''} in other zones. Remove the conflicting ZIP codes (highlighted in red below) to proceed.`,
        variant: 'destructive',
        duration: 10000
      });
      return;
    }

    try {
      setCreating(true);

      // Determine shape type from geometry
      // Note: Freehand polylines are converted to polygons by normalizeGeometry
      const shapeType =
        drawnGeometry.type === 'Polygon'
          ? 'polygon'
          : drawnGeometry.type === 'MultiPolygon'
            ? 'multipolygon'
            : 'polygon'; // Default to polygon

      // Geometry only. Recall counts are NOT denormalized into metadata any more: the
      // client wrote metadata.recallCount here while a background task overwrote
      // total_recalls_at_creation with a server-side figure, so the two silently diverged
      // (22 zones at last audit). Size is now always derived from total_recalls_current,
      // which the server refreshes.
      const metadata = {
        geometry: drawnGeometry as any,
        shape_type: shapeType as any
      };

      const createdZone = await createZone.mutateAsync({
        data: {
          zone_name: zoneName.trim(),
          ...(zoneDescription.trim() && { description: zoneDescription.trim() }),
          region: zoneRegion.trim(),
          country: 'US',
          status: 'active',
          oem: selectedOem, // NEW: Set zone's OEM
          zip_codes: zoneStats.zipCodes,
          metadata,
          total_recalls_at_creation: zoneStats.totalRecalls,
          unique_vins_at_creation: zoneStats.uniqueVins
        },
        userId: session?.user?.id
      });

      toast({
        title: 'Zone Created',
        description: `Zone "${zoneName}" has been created successfully with ${zoneStats.zipCodes.length} ZIP codes.`
      });

      // Refresh available regions if a new region was added
      if (zoneRegion && !availableRegions.includes(zoneRegion)) {
        const regions = await tenantAPI.getUniqueRegions();
        setAvailableRegions(regions);
      }

      // Reset state and clear the drawn shape
      setDialogOpen(false);
      setZoneName('');
      setZoneDescription('');
      setZoneRegion('');
      setDrawnGeometry(null);
      setZoneStats(null);

      // Clear the drawn shape from the map
      setShouldClearDrawnShapes(true);
      // Reset the flag after a brief delay
      setTimeout(() => setShouldClearDrawnShapes(false), 100);

      // Zone is automatically added to cache by the mutation

      // Automatically redefine zone boundary to match ZIP code boundaries
      // Use the ZIP codes that were already fetched and stored in the zone
      // Do this asynchronously after the dialog is closed
      setUpdatingZoneBoundaries(true);
      try {
        const { createZoneBoundaryFromZipCodes } = await import('../services/zipCodeBoundaries');
        // Use ZIP codes from the created zone (already persisted in Supabase)
        const zipCodesToUse = createdZone.zip_codes || zoneStats.zipCodes;

        const zipBasedGeometry = await createZoneBoundaryFromZipCodes(zipCodesToUse);

        if (zipBasedGeometry) {
          // Update zone with ZIP-code-based geometry
          const updatedMetadata = {
            ...metadata,
            geometry: zipBasedGeometry,
            original_geometry: metadata.geometry, // Keep original for reference
            boundary_type: 'zip_based' // Mark as ZIP-based boundary
          };

          await updateZone.mutateAsync({
            id: createdZone.id,
            data: {
              metadata: updatedMetadata
            },
            userId: session?.user?.id
          });

          toast({
            title: 'Zone Boundary Updated',
            description:
              'Zone boundary has been automatically adjusted to match ZIP code boundaries.'
          });

          // Zone is automatically updated in cache by the mutation
        } else {
        }
      } catch (error) {
        toast({
          title: 'Zone Boundary Update Failed',
          description: 'Could not adjust zone to ZIP code boundaries. Using original shape.',
          variant: 'destructive'
        });
      } finally {
        setUpdatingZoneBoundaries(false);
      }
    } catch (error: any) {
      let errorMessage = 'Failed to create zone';
      if (error.message?.includes('409') || error.message?.includes('conflict')) {
        errorMessage =
          'Zone name already exists or conflicts with existing data. Please try a different name.';
      } else if (error.message?.includes('payload too large') || error.message?.includes('413')) {
        errorMessage =
          'Zone is too large to create. Please reduce the number of ZIP codes or draw a smaller area.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast({
        title: 'Error Creating Zone',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setCreating(false);
    }
  }, [
    zoneName,
    zoneRegion,
    zoneDescription,
    drawnGeometry,
    zoneStats,
    creating,
    toast,
    checkZipCodeOverlaps,
    createZone,
    updateZone,
    session?.user?.id,
    campaignPreferences
  ]);

  // Remove ZIP code from zone during creation
  const handleRemoveZipCode = useCallback(
    async (zipCodeToRemove: string) => {
      if (!zoneStats) return;

      const updatedZipCodes = zoneStats.zipCodes.filter(zip => zip !== zipCodeToRemove);

      if (updatedZipCodes.length === 0) {
        toast({
          title: 'Cannot Remove ZIP Code',
          description: 'A zone must contain at least one ZIP code.',
          variant: 'destructive'
        });
        return;
      }

      try {
        // Check for overlaps with updated ZIP codes
        const overlappingZones = checkZipCodeOverlaps(updatedZipCodes);

        // Recalculate statistics with the updated ZIP codes
        // Pass selectedOem to filter recalls by OEM brands
        const stats = await calculateZoneStatistics(
          updatedZipCodes,
          session?.user?.id,
          campaignPreferences as any,
          selectedSnapshotDate,
          selectedOem
        );

        setZoneStats({
          zipCodes: updatedZipCodes,
          totalRecalls: stats.totalRecalls,
          uniqueVins: stats.uniqueVins,
          overlappingZones
        });

        toast({
          title: 'ZIP Code Removed',
          description: `ZIP code ${zipCodeToRemove} has been removed from the zone.`
        });
      } catch (error: any) {
        toast({
          title: 'Error Updating Zone',
          description: error.message || 'Failed to update zone statistics',
          variant: 'destructive'
        });
      }
    },
    [zoneStats, session?.user?.id, toast, campaignPreferences]
  );

  // Cancel zone creation
  const handleCancel = useCallback(() => {
    setDialogOpen(false);
    setZoneName('');
    setZoneDescription('');
    setZoneRegion('');
    setDrawnGeometry(null);
    setZoneStats(null);
  }, []);

  // Handle zone deletion
  const handleDeleteZone = useCallback(async (zone: Zone) => {
    setZoneToDelete(zone);
    setDeleteDialogOpen(true);
  }, []);

  const confirmDeleteZone = useCallback(async () => {
    if (!zoneToDelete) return;

    try {
      setCreating(true);
      await deleteZone.mutateAsync({ id: zoneToDelete.id, hard: true, userId: session?.user?.id }); // Hard delete - completely removes from database

      toast({
        title: 'Zone Deleted',
        description: `Zone "${zoneToDelete.zone_name}" has been deleted.`
      });

      // Zone is automatically removed from cache by the mutation
      setDeleteDialogOpen(false);
      setZoneToDelete(null);
    } catch (error: any) {
      toast({
        title: 'Error Deleting Zone',
        description: error.message || 'Failed to delete zone',
        variant: 'destructive'
      });
    } finally {
      setCreating(false);
    }
  }, [zoneToDelete, toast, deleteZone, session?.user?.id]);

  // Remove ZIP code from existing zone
  const handleRemoveZipCodeFromExistingZone = useCallback(
    async (zone: Zone, zipCodeToRemove: string) => {
      const currentZipCodes = zone.zip_codes || [];
      const updatedZipCodes = currentZipCodes.filter(zip => zip !== zipCodeToRemove);

      if (updatedZipCodes.length === 0) {
        toast({
          title: 'Cannot Remove ZIP Code',
          description: 'A zone must contain at least one ZIP code. Delete the zone instead.',
          variant: 'destructive'
        });
        return;
      }

      try {
        setUpdatingZoneBoundaries(true);

        // Check if the updated ZIP codes would cause overlaps (shouldn't happen since we're removing, but good to check)
        const overlappingZones = checkZipCodeOverlaps(updatedZipCodes, zone.id);
        if (overlappingZones.length > 0) {
          toast({
            title: 'Cannot Update Zone',
            description: 'This change would create ZIP code overlaps with existing zones.',
            variant: 'destructive'
          });
          return;
        }

        // No recall recount here any more: the count is derived server-side from the
        // zone's ZIP set, so removing a ZIP is picked up by the next refresh. Dropping the
        // call also takes a slow RPC off this path.

        // Create new geometry from updated ZIP codes
        const { createZoneBoundaryFromZipCodes } = await import('../services/zipCodeBoundaries');
        const zipBasedGeometry = await createZoneBoundaryFromZipCodes(updatedZipCodes);

        // Update zone geometry only — recall counts are no longer denormalized into
        // metadata. The server recomputes them from the new ZIP set on the next refresh.
        const updatedMetadata = {
          ...(zone.metadata || {}),
          geometry: zipBasedGeometry || (zone.metadata || {})['geometry'],
          boundary_type: zipBasedGeometry ? 'zip_based' : (zone.metadata || {})['boundary_type']
        } as any;

        // Update the zone in the database using cached mutation
        await updateZone.mutateAsync({
          id: zone.id,
          data: {
            zip_codes: updatedZipCodes,
            metadata: updatedMetadata
          },
          userId: session?.user?.id
        });

        toast({
          title: 'ZIP Code Removed',
          description: `ZIP code ${zipCodeToRemove} has been removed from "${zone.zone_name}". Zone boundaries have been updated.`
        });

        // Zone is automatically updated in cache by the mutation
      } catch (error: any) {
        toast({
          title: 'Error Removing ZIP Code',
          description: error.message || 'Failed to remove ZIP code from zone',
          variant: 'destructive'
        });
      } finally {
        setUpdatingZoneBoundaries(false);
      }
    },
    [session?.user?.id, toast, updateZone, checkZipCodeOverlaps, campaignPreferences]
  );

  // Check for ZIP code overlaps with existing zones

  if (recallsError) {
    return (
      <Card>
        <CardContent className='pt-6'>
          <div className='text-center text-destructive'>
            <p>Error loading recalls: {recallsError}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div
      className={`h-full flex flex-col ${isFullscreen ? 'zone-map-fullscreen' : ''}`}
      style={
        isFullscreen
          ? {
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'var(--background)'
            }
          : undefined
      }
    >
      {/* Header Row - Dealers, Recalls, Zones Count, Refresh, Snapshot, and Helper Text */}
      <div
        className='flex items-center justify-between p-4 border-b bg-background'
        style={{ zIndex: 1000, position: 'relative' }}
      >
        <div className='flex items-center gap-6'>
          <div className='flex items-center gap-2'>
            <Map className='h-4 w-4 text-muted-foreground' />
            <span className='text-sm font-medium text-foreground'>
              {recallsLoading
                ? 'Loading...'
                : `${totalDealers.toLocaleString()} dealers • ${totalRecalls.toLocaleString()} recalls`}
            </span>
          </div>
          <div className='flex items-center gap-2'>
            <Users className='h-4 w-4 text-muted-foreground' />
            <span className='text-sm font-medium text-foreground'>
              {zonesInViewport.length} {zonesInViewport.length === 1 ? 'zone' : 'zones'} created
            </span>
          </div>
          {/* Bubbles Toggle */}
          <div className='flex items-center gap-2'>
            <CircleDot className='h-4 w-4 text-muted-foreground' />
            <Label htmlFor='show-bubbles' className='text-sm cursor-pointer'>
              Bubbles
            </Label>
            <Switch id='show-bubbles' checked={showBubbles} onCheckedChange={setShowBubbles} />
          </div>
          {/* Uniform Color Toggle */}
          <div className='flex items-center gap-2'>
            <Paintbrush className='h-4 w-4 text-muted-foreground' />
            <Label htmlFor='uniform-color' className='text-sm cursor-pointer'>
              Uniform
            </Label>
            <Switch id='uniform-color' checked={uniformColor} onCheckedChange={setUniformColor} />
          </div>
        </div>
        <div className='flex items-center gap-4'>
          {/* Refresh Button */}
          <button
            onClick={async () => {
              setIsRefreshing(true);
              toast({
                title: 'Refreshing Data',
                description: 'Updating map data and clearing cache...'
              });
              try {
                await refreshRecallData();
                toast({
                  title: 'Data Refreshed',
                  description: 'Map data has been updated successfully.',
                  variant: 'default'
                });
              } catch (error: any) {
                toast({
                  title: 'Refresh Failed',
                  description: error.message || 'Failed to refresh map data',
                  variant: 'destructive'
                });
              } finally {
                setIsRefreshing(false);
              }
            }}
            disabled={isRefreshing || recallsLoading}
            className='flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
            title='Refresh map data and clear cache'
          >
            <RefreshCw
              className={`h-4 w-4 ${isRefreshing || recallsLoading ? 'animate-spin' : ''}`}
            />
            <span>Refresh Data</span>
            {(isRefreshing || recallsLoading) && (
              <span className='text-xs text-muted-foreground'>Refreshing...</span>
            )}
          </button>

          {/* Snapshot Date Selector for Control Center */}
          <div className='flex items-center gap-2'>
            <Calendar className='h-4 w-4 text-muted-foreground' />
            <Select
              value={selectedSnapshotDate || 'latest'}
              onValueChange={value => {
                setSelectedSnapshotDate(value === 'latest' ? null : value);
              }}
              disabled={loadingSnapshots}
            >
              <SelectTrigger className='w-[180px]'>
                <SelectValue placeholder='Select snapshot date' />
              </SelectTrigger>
              <SelectContent className='z-[1000]'>
                <SelectItem value='latest'>
                  Latest Snapshot
                  {availableSnapshots.length > 0 && (
                    <span className='text-xs text-muted-foreground ml-2'>
                      ({availableSnapshots[0]?.record_count.toLocaleString()} recalls)
                    </span>
                  )}
                </SelectItem>
                {availableSnapshots.map(snapshot => (
                  <SelectItem key={snapshot.snapshot_date} value={snapshot.snapshot_date}>
                    {new Date(snapshot.snapshot_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}{' '}
                    <span className='text-xs text-muted-foreground ml-2'>
                      ({snapshot.record_count.toLocaleString()} recalls)
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* OEM Filter */}
          <div className='flex items-center gap-2'>
            <Select value={selectedOem} onValueChange={setSelectedOem}>
              <SelectTrigger className='w-[130px]'>
                <SelectValue placeholder='Select OEM' />
              </SelectTrigger>
              <SelectContent className='z-[10000] max-h-[300px]'>
                {KNOWN_OEMS.filter(oem => oem !== 'Other').map(oem => (
                  <SelectItem key={oem} value={oem}>
                    {oem}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Region Filter */}
          <div className='flex items-center gap-2'>
            <Select value={regionFilter} onValueChange={setRegionFilter}>
              <SelectTrigger className='w-[140px]'>
                <SelectValue placeholder='All Regions' />
              </SelectTrigger>
              <SelectContent className='z-[10000]'>
                <SelectItem value='all'>All Regions</SelectItem>
                {availableRegions.map(region => (
                  <SelectItem key={region} value={region}>
                    {region}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Zone Filter (Multi-select) */}
          <div className='flex items-center gap-2'>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant='outline' className='w-[180px] justify-between text-sm font-normal'>
                  {zoneFilter.length === 0
                    ? `All ${selectedOem} Zones (${zones.filter(z => z.oem === selectedOem).length})`
                    : `${zoneFilter.length} zone${zoneFilter.length !== 1 ? 's' : ''} selected`}
                  <ChevronDown className='h-4 w-4 opacity-50' />
                </Button>
              </PopoverTrigger>
              <PopoverContent className='w-[240px] p-0 z-[10000]' align='start'>
                <div className='flex items-center justify-between p-2 border-b'>
                  <Button
                    variant='ghost'
                    size='sm'
                    className='h-7 text-xs'
                    onClick={() => {
                      const allIds = zones
                        .filter(z => z.oem === selectedOem)
                        .filter(z => regionFilter === 'all' || z.region === regionFilter)
                        .map(z => z.id);
                      setZoneFilter(allIds);
                    }}
                  >
                    Select All
                  </Button>
                  <Button
                    variant='ghost'
                    size='sm'
                    className='h-7 text-xs'
                    onClick={() => setZoneFilter([])}
                  >
                    Clear
                  </Button>
                </div>
                <div className='max-h-[300px] overflow-y-auto p-2 space-y-1'>
                  {zones
                    .filter(zone => zone.oem === selectedOem)
                    .filter(zone => regionFilter === 'all' || zone.region === regionFilter)
                    .sort((a, b) => (a.zone_name || '').localeCompare(b.zone_name || ''))
                    .map(zone => (
                      <label
                        key={zone.id}
                        className='flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer'
                      >
                        <Checkbox
                          checked={zoneFilter.includes(zone.id)}
                          onCheckedChange={checked => {
                            setZoneFilter(prev =>
                              checked ? [...prev, zone.id] : prev.filter(id => id !== zone.id)
                            );
                          }}
                        />
                        <span className='text-sm truncate'>{zone.zone_name}</span>
                        {zone.region && (
                          <span className='text-xs text-muted-foreground ml-auto flex-shrink-0'>
                            {zone.region}
                          </span>
                        )}
                      </label>
                    ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <div className='text-xs text-muted-foreground'>
            Use drawing tools to create zones • Use trash tool to delete zones
          </div>
        </div>
      </div>

      {/* Filter Information Panel */}
      <Collapsible open={filterPanelOpen} onOpenChange={setFilterPanelOpen}>
        <CollapsibleTrigger asChild>
          <div className='flex items-center justify-between p-3 border-b bg-background hover:bg-muted cursor-pointer'>
            <div className='flex items-center gap-2'>
              <Filter className='h-4 w-4 text-primary' />
              <span className='text-sm font-medium text-foreground'>Data Filters Applied</span>
              {(activeCampaignFilters > 0 || excludedDealerCount > 0) && (
                <Badge variant='secondary'>
                  {activeCampaignFilters + (excludedDealerCount > 0 ? 1 : 0)} active
                </Badge>
              )}
            </div>
            <ChevronDown
              className={`h-4 w-4 text-primary transition-transform ${filterPanelOpen ? 'rotate-180' : ''}`}
            />
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className='p-4 bg-background border-b space-y-3'>
            <div className='text-xs font-medium text-foreground uppercase tracking-wider'>
              Active Filters Affecting Map Data
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
              {/* Campaign Preferences */}
              <div className='flex items-start gap-2'>
                <Settings className='h-4 w-4 text-primary mt-0.5 flex-shrink-0' />
                <div>
                  <div className='text-sm font-medium text-foreground'>Campaign Preferences</div>
                  <div className='text-xs text-muted-foreground'>
                    {activeCampaignFilters > 0
                      ? `${activeCampaignFilters} campaign${activeCampaignFilters === 1 ? '' : 's'} with custom skill level or mobile eligibility filters`
                      : 'No campaign-specific filters applied'}
                  </div>
                </div>
              </div>

              {/* Excluded Dealers */}
              <div className='flex items-start gap-2'>
                <Users className='h-4 w-4 text-primary mt-0.5 flex-shrink-0' />
                <div>
                  <div className='text-sm font-medium text-foreground'>Dealer Exclusions</div>
                  <div className='text-xs text-muted-foreground'>
                    {excludedDealerCount > 0
                      ? `${excludedDealerCount} dealer${excludedDealerCount === 1 ? '' : 's'} excluded from zone calculations`
                      : 'No dealers excluded from calculations'}
                  </div>
                </div>
              </div>
            </div>

            <div className='mt-3 p-2 bg-accent/50 rounded border border-border'>
              <div className='text-xs text-accent-foreground'>
                <Info className='h-3 w-3 inline mr-1' />
                <strong>How this affects zones:</strong> Only dealers matching your campaign
                preferences and not in the excluded list will appear on the map and be included in
                zone recall calculations. Zone statistics reflect the same filters used in the
                Simple Recall Map.
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Full-screen map */}
      <div
        className={`flex-1 relative ${isFullscreen ? 'zone-map-fullscreen-container' : ''}`}
        style={isFullscreen ? { height: '100vh', width: '100vw' } : undefined}
      >
        {/* Fullscreen toggle button - positioned in top-right corner */}
        <button
          onClick={() => setIsFullscreen(!isFullscreen)}
          className='absolute top-4 right-4 z-[1000] bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-border p-2 hover:bg-accent transition-colors'
          title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        >
          {isFullscreen ? (
            <Minimize2 className='h-4 w-4 text-foreground' />
          ) : (
            <Maximize2 className='h-4 w-4 text-foreground' />
          )}
        </button>
        {(loadingZones || updatingZoneBoundaries) && (
          <div className='absolute top-4 left-4 z-[1000] bg-white rounded-lg shadow-lg border p-3'>
            <div className='flex items-center gap-2'>
              <Loader2 className='h-4 w-4 animate-spin text-primary' />
              <span className='text-sm text-muted-foreground'>
                {updatingZoneBoundaries ? 'Updating zone boundaries...' : 'Loading zones...'}
              </span>
            </div>
          </div>
        )}
        <ZoneDrawingMapOptimized
          dealerLocations={filteredDealerLocations}
          zones={zonesInViewport}
          onShapeDrawn={handleShapeDrawn}
          onMapBoundsChange={handleMapBoundsChange}
          selectedZoneId={selectedZoneId}
          onZoneDelete={zoneId => {
            const zone = zones.find(z => z.id === zoneId);
            if (zone) handleDeleteZone(zone);
          }}
          onRemoveZipCode={handleRemoveZipCodeFromExistingZone}
          loading={recallsLoading}
          clearDrawnShapes={shouldClearDrawnShapes}
          showBubbles={showBubbles}
          uniformColor={uniformColor}
        />
      </div>

      {/* Add animation styles */}
      <style>{styles}</style>
      {/* Fullscreen styles */}
      <style>{`
        .zone-map-fullscreen {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          z-index: 99999 !important;
          background: var(--background) !important;
          width: 100vw !important;
          height: 100vh !important;
          isolation: isolate !important;
        }
        .zone-map-fullscreen-container {
          height: 100vh !important;
          width: 100vw !important;
        }
        .zone-map-fullscreen > div:first-child {
          background-color: var(--background) !important;
          z-index: 99999 !important;
        }
        /* Hide sidebar and other layout elements when fullscreen */
        body:has(.zone-map-fullscreen) .sidebar,
        body:has(.zone-map-fullscreen) [class*="sidebar"],
        body:has(.zone-map-fullscreen) header,
        body:has(.zone-map-fullscreen) [class*="StandaloneSidebar"] {
          display: none !important;
        }
        /* Ensure dialog appears above fullscreen container */
        body:has(.zone-map-fullscreen) [data-radix-portal] {
          z-index: 100000 !important;
        }
        body:has(.zone-map-fullscreen) [data-radix-dialog-overlay] {
          z-index: 100000 !important;
        }
        body:has(.zone-map-fullscreen) [data-radix-dialog-content] {
          z-index: 100001 !important;
        }
        /* Ensure Select dropdowns appear above fullscreen container */
        body:has(.zone-map-fullscreen) [data-radix-popper-content-wrapper] {
          z-index: 100000 !important;
        }
      `}</style>

      {/* Zone Creation Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className='max-w-lg z-[100000]'>
          <DialogHeader>
            <DialogTitle className='text-xl'>Create New Zone</DialogTitle>
            <DialogDescription className='text-base'>
              Define your zone details. This area includes{' '}
              <strong>{zoneStats?.zipCodes.length || 0} ZIP codes</strong> with{' '}
              <strong>{zoneStats?.totalRecalls.toLocaleString() || 0} recalls</strong>.
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-6 py-4'>
            {/* Zone Statistics - More prominent */}
            {zoneStats && (
              <>
                {/* Warning for Extra Large zones */}
                {zoneStats.totalRecalls >= 700 && (
                  <div className='rounded-lg border-2 border-destructive/20 bg-destructive/5 p-4'>
                    <div className='flex items-center gap-2 mb-2'>
                      <span className='text-destructive text-lg'>⚠️</span>
                      <span className='font-semibold text-destructive'>Zone Too Large</span>
                    </div>
                    <p className='text-sm text-muted-foreground'>
                      This zone has {zoneStats.totalRecalls.toLocaleString()} recalls (Extra Large).
                      Consider breaking it into smaller zones for better management.
                    </p>
                    <p className='text-xs text-muted-foreground mt-2'>
                      Recommended: Small (&lt; 300), Medium (300-499), or Large (500-699)
                    </p>
                  </div>
                )}

                {/* ZIP Code Overlap Warning - Always show if overlaps exist */}
                {(() => {
                  console.log('[ZoneMapTab] Checking overlap warning - zoneStats:', {
                    hasZoneStats: !!zoneStats,
                    hasOverlappingZones: !!zoneStats?.overlappingZones,
                    overlappingZones: zoneStats?.overlappingZones,
                    isArray: Array.isArray(zoneStats?.overlappingZones),
                    length: zoneStats?.overlappingZones?.length || 0,
                    type: typeof zoneStats?.overlappingZones
                  });

                  const hasOverlaps =
                    zoneStats?.overlappingZones &&
                    Array.isArray(zoneStats.overlappingZones) &&
                    zoneStats.overlappingZones.length > 0;

                  console.log('[ZoneMapTab] Overlap warning check result:', hasOverlaps);

                  if (hasOverlaps) {
                    console.log(
                      '[ZoneMapTab] RENDERING overlap warning section with',
                      zoneStats.overlappingZones.length,
                      'overlaps'
                    );
                  } else {
                    console.log('[ZoneMapTab] NOT rendering overlap warning - condition failed');
                  }

                  return hasOverlaps ? (
                    <div className='rounded-lg border-2 border-destructive/20 bg-destructive/5 p-4'>
                      <div className='flex items-center gap-2 mb-2'>
                        <span className='text-destructive text-lg'>🚫</span>
                        <span className='font-semibold text-destructive'>
                          ZIP Code Overlap Detected (
                          {zoneStats.overlappingZones.reduce(
                            (sum, z) => sum + z.overlappingZips.length,
                            0
                          )}{' '}
                          conflicts)
                        </span>
                      </div>
                      <p className='text-sm text-muted-foreground mb-3'>
                        Some ZIP codes already exist in other zones. Each ZIP code can only belong
                        to one zone.{' '}
                        <strong>
                          Remove the conflicting ZIP codes (highlighted in red below) to proceed
                          with zone creation.
                        </strong>
                      </p>
                      <div className='space-y-2'>
                        {zoneStats.overlappingZones.map((overlap, index) => (
                          <div key={index} className='bg-destructive/10 rounded p-2'>
                            <div className='text-sm font-medium text-destructive mb-1'>
                              Zone: {overlap.zoneName}
                            </div>
                            <div className='text-xs text-muted-foreground'>
                              Conflicting ZIP codes: {overlap.overlappingZips.join(', ')}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className='mt-3 flex items-center justify-between'>
                        <p className='text-xs text-destructive font-medium'>
                          Please adjust your selection to remove overlapping areas.
                        </p>
                        <Button
                          size='sm'
                          variant='destructive'
                          onClick={() => {
                            // Remove all conflicting zip codes
                            const conflictingZips = new Set(
                              zoneStats.overlappingZones?.flatMap(z => z.overlappingZips) || []
                            );
                            const filteredZips = zoneStats.zipCodes.filter(
                              zip => !conflictingZips.has(zip)
                            );
                            if (filteredZips.length === 0) {
                              toast({
                                title: 'Cannot Remove All ZIP Codes',
                                description:
                                  'Removing all conflicting ZIP codes would leave the zone empty.',
                                variant: 'destructive'
                              });
                              return;
                            }
                            // Update zone stats without conflicting zips
                            setZoneStats(prev => ({
                              ...prev!,
                              zipCodes: filteredZips,
                              overlappingZones: [],
                              isLoadingStats: true
                            }));
                            calculateZoneStatistics(
                              filteredZips,
                              session?.user?.id,
                              campaignPreferences,
                              selectedSnapshotDate,
                              selectedOem
                            )
                              .then(stats => {
                                setZoneStats(prev => ({
                                  ...prev!,
                                  totalRecalls: stats.totalRecalls,
                                  uniqueVins: stats.uniqueVins,
                                  isLoadingStats: false
                                }));
                                toast({
                                  title: 'Conflicting ZIP Codes Removed',
                                  description: `Removed ${conflictingZips.size} conflicting ZIP code(s).`
                                });
                              })
                              .catch(error => {
                                console.error('[ZoneMapTab] Error recalculating stats:', error);
                                setZoneStats(prev => ({
                                  ...prev!,
                                  isLoadingStats: false
                                }));
                              });
                          }}
                          disabled={creating}
                        >
                          Quick Remove Conflicting
                        </Button>
                      </div>
                    </div>
                  ) : null;
                })()}

                <div
                  className={`rounded-lg border-2 p-4 ${zoneStats.totalRecalls >= 700 ? 'border-destructive/20 bg-destructive/5' : 'border-primary/20 bg-primary/5'}`}
                >
                  <div className='grid grid-cols-2 gap-4 text-center'>
                    <div>
                      <div className='text-2xl font-bold text-primary'>
                        {zoneStats.zipCodes.length}
                      </div>
                      <div className='text-xs text-muted-foreground'>ZIP Codes</div>
                    </div>
                    <div>
                      {zoneStats.isLoadingStats ? (
                        <div className='flex items-center justify-center gap-2'>
                          <Loader2 className='h-5 w-5 animate-spin text-primary' />
                          <span className='text-sm text-muted-foreground'>Calculating...</span>
                        </div>
                      ) : zoneStats.statsError ? (
                        <div className='flex flex-col items-center justify-center gap-1'>
                          <div className='text-destructive text-sm font-medium'>Failed to load</div>
                          <div className='text-xs text-muted-foreground'>
                            Check console for details
                          </div>
                        </div>
                      ) : (
                        <>
                          <div
                            className={`text-2xl font-bold ${zoneStats.totalRecalls >= 700 ? 'text-destructive' : 'text-primary'}`}
                          >
                            {zoneStats.totalRecalls.toLocaleString()}
                          </div>
                          <div className='text-xs text-muted-foreground'>Total Recalls</div>
                        </>
                      )}
                    </div>
                  </div>
                  {!zoneStats.isLoadingStats && !zoneStats.statsError && (
                    <div className='mt-3 text-center'>
                      <span
                        className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${
                          zoneStats.totalRecalls >= 700
                            ? 'bg-destructive/10 text-destructive'
                            : 'bg-primary/10 text-primary'
                        }`}
                      >
                        {getZoneSizeLabel(zoneStats.totalRecalls)} Zone
                      </span>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* OEM indicator - shows which OEM this zone will be created for */}
            <div className='flex items-center gap-2 py-2 px-3 bg-muted rounded-md'>
              <span className='text-sm text-muted-foreground'>Creating zone for:</span>
              <Badge variant='secondary' className='font-medium'>
                {selectedOem}
              </Badge>
            </div>

            {/* Form fields - simplified */}
            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='zone-name'>Zone Name *</Label>
                <Input
                  id='zone-name'
                  value={zoneName}
                  onChange={e => setZoneName(e.target.value)}
                  placeholder='e.g., Tampa Bay'
                  disabled={creating}
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='zone-region'>Region *</Label>
                <Input
                  id='zone-region'
                  value={zoneRegion}
                  onChange={e => setZoneRegion(e.target.value)}
                  placeholder='e.g., Southeast'
                  disabled={creating}
                />
                <p className='text-xs text-muted-foreground'>
                  Multiple zones can share the same region
                </p>
              </div>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='zone-description'>Description (Optional)</Label>
              <Input
                id='zone-description'
                value={zoneDescription}
                onChange={e => setZoneDescription(e.target.value)}
                placeholder='e.g., High priority area for recall campaigns'
                disabled={creating}
              />
            </div>

            {/* Included ZIP Codes */}
            {zoneStats && zoneStats.zipCodes && zoneStats.zipCodes.length > 0 && (
              <div className='space-y-2 border-t pt-4'>
                <Label className='text-sm font-medium'>
                  Included ZIP Codes ({zoneStats.zipCodes.length})
                  <span className='text-xs text-muted-foreground ml-2'>Click × to remove</span>
                </Label>
                <div className='max-h-32 overflow-y-auto rounded-md border bg-muted/30 p-3'>
                  <div className='flex flex-wrap gap-2'>
                    {(() => {
                      // Extract all conflicting ZIP codes from overlapping zones
                      const overlappingZones = zoneStats.overlappingZones || [];
                      const conflictingZips = new Set<string>();

                      console.log(
                        '[ZoneMapTab] ZIP Codes Section - zoneStats.overlappingZones:',
                        zoneStats.overlappingZones
                      );
                      console.log(
                        '[ZoneMapTab] ZIP Codes Section - overlappingZones array:',
                        overlappingZones
                      );

                      overlappingZones.forEach(overlap => {
                        console.log('[ZoneMapTab] Processing overlap:', overlap);
                        if (Array.isArray(overlap.overlappingZips)) {
                          overlap.overlappingZips.forEach(zip => {
                            console.log('[ZoneMapTab] Adding conflicting ZIP:', zip);
                            conflictingZips.add(zip);
                          });
                        }
                      });

                      // Debug logging
                      console.log('[ZoneMapTab] Rendering ZIP codes section:', {
                        zoneStatsOverlappingZones: zoneStats.overlappingZones,
                        overlappingZonesCount: overlappingZones.length,
                        conflictingZipsCount: conflictingZips.size,
                        conflictingZips: Array.from(conflictingZips),
                        allZips: zoneStats.zipCodes.length,
                        allZipsList: zoneStats.zipCodes.slice(0, 10)
                      });

                      // Sort ZIP codes: conflicting ones first, then non-conflicting
                      const sortedZipCodes = [...zoneStats.zipCodes].sort((a, b) => {
                        const aIsConflicting = conflictingZips.has(a);
                        const bIsConflicting = conflictingZips.has(b);
                        if (aIsConflicting && !bIsConflicting) return -1; // a comes first
                        if (!aIsConflicting && bIsConflicting) return 1; // b comes first
                        return a.localeCompare(b); // Both same type, sort alphabetically
                      });

                      return sortedZipCodes.map(zipCode => {
                        // Check if this ZIP code is conflicting
                        const isConflicting = conflictingZips.has(zipCode);

                        // Debug log for conflicting ZIPs
                        if (isConflicting) {
                          console.log(
                            '[ZoneMapTab] ⚠️ CONFLICTING ZIP CODE FOUND - Rendering in RED:',
                            zipCode
                          );
                        }

                        return (
                          <div key={zipCode} className='relative group'>
                            <Badge
                              variant={isConflicting ? 'destructive' : 'secondary'}
                              className={cn(
                                'font-mono text-xs pr-6',
                                isConflicting && [
                                  'zip-bubble-animation',
                                  '!bg-red-600',
                                  '!text-white',
                                  '!border-2',
                                  '!border-red-800',
                                  'border-solid',
                                  'shadow-lg',
                                  'ring-2',
                                  'ring-red-400'
                                ]
                              )}
                              title={
                                isConflicting
                                  ? 'This ZIP code conflicts with another zone - click × to remove'
                                  : undefined
                              }
                            >
                              {zipCode}
                            </Badge>
                            <button
                              onClick={() => handleRemoveZipCode(zipCode)}
                              disabled={creating || zoneStats.zipCodes.length <= 1}
                              className={`absolute -top-1 -right-1 w-4 h-4 rounded-full ${
                                isConflicting ? 'bg-destructive/80' : 'bg-destructive'
                              } text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity ${
                                zoneStats.zipCodes.length <= 1
                                  ? 'cursor-not-allowed opacity-30'
                                  : 'hover:bg-destructive/80'
                              }`}
                              title={
                                zoneStats.zipCodes.length <= 1
                                  ? 'Cannot remove the last ZIP code'
                                  : `Remove ${zipCode}`
                              }
                            >
                              <X className='h-2.5 w-2.5' />
                            </button>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className='gap-3'>
            <Button variant='outline' onClick={handleCancel} disabled={creating}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateZone}
              disabled={
                creating ||
                !zoneName.trim() ||
                !zoneRegion.trim() ||
                zoneStats?.isLoadingStats ||
                zoneStats?.statsError
              }
              className='min-w-24'
            >
              {creating ? (
                <>
                  <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                  Creating...
                </>
              ) : (
                'Create Zone'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Permanently Delete Zone</DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete the zone &quot;{zoneToDelete?.zone_name}
              &quot;? This will completely remove the zone, its boundaries, and all associated data.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setDeleteDialogOpen(false)}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button variant='destructive' onClick={confirmDeleteZone} disabled={creating}>
              {creating && <Loader2 className='h-4 w-4 mr-2 animate-spin' />}
              Delete Zone
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
